import { inject } from '@angular/core';
import { HttpErrorResponse, HttpInterceptorFn, HttpStatusCode } from '@angular/common/http';
import { catchError, throwError } from 'rxjs';
import { SnackbarApi } from '@shared/services/snackbar.api';
import { AuthStore } from '@shared/modules/auth/store/auth.store';

const SERVER_MESSAGE_STATUSES: HttpStatusCode[] = [
  HttpStatusCode.NotFound,
  HttpStatusCode.BadRequest,
  HttpStatusCode.PayloadTooLarge,
  HttpStatusCode.Conflict,
  HttpStatusCode.Forbidden,
];

const HANDLED_STATUSES: HttpStatusCode[] = [...SERVER_MESSAGE_STATUSES, HttpStatusCode.Unauthorized];

export const errorInterceptor: HttpInterceptorFn = (request, next) => {
  const snackbarApi = inject(SnackbarApi);
  const authStore = inject(AuthStore);

  return next(request).pipe(
    catchError((error: HttpErrorResponse) => {
      // network down, CORS block, DNS
      if (error.status === 0) {
        snackbarApi.failure('Cannot reach the server. Check your connection and try again.');
        return throwError(() => error);
      }

      if (SERVER_MESSAGE_STATUSES.includes(error.status)) {
        showServerMessage(snackbarApi, error);
      }

      if (error.status === HttpStatusCode.TooManyRequests) {
        const message: unknown = error.error?.message;
        snackbarApi.failure(typeof message === 'string' && message ? message : 'Too many attempts. Try again later.');
      } else if (error.status >= HttpStatusCode.InternalServerError) {
        snackbarApi.failure('Something went wrong, please try again.');
      } else if (error.status >= HttpStatusCode.BadRequest && !HANDLED_STATUSES.includes(error.status)) {
        snackbarApi.failure('Request failed, please try again.');
      }

      const isLogout = error.url?.includes('logout');
      if (error.status === HttpStatusCode.Unauthorized && !isLogout) {
        authStore.logout();
      }

      return throwError(() => error);
    })
  );
};

function showServerMessage(snackbarApi: SnackbarApi, error: HttpErrorResponse): void {
  const message: unknown = error.error?.message;

  if (Array.isArray(message)) {
    message.forEach((res: string | { message: string }) => {
      if (typeof res === 'string') snackbarApi.failure(res);
    });
  } else if (typeof message === 'string') {
    snackbarApi.failure(message);
  } else {
    snackbarApi.failure(error.statusText);
  }
}
